/**
 * useKeyboardShortcuts — Global keyboard shortcut handler.
 * Provides command palette toggle and navigation shortcuts.
 */

import { useEffect, useCallback, useState } from "react";

interface ShortcutHandlers {
  onCommandPalette?: () => void;
  onToggleMapFullscreen?: () => void;
  onResetZoom?: () => void;
  navigate?: (page: any) => void;
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen((prev) => !prev);
    handlers.onCommandPalette?.();
  }, [handlers]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        // Allow Escape to close command palette even in inputs
        if (e.key === "Escape" && commandPaletteOpen) {
          setCommandPaletteOpen(false);
        }
        return;
      }

      switch (e.key) {
        case "/":
          e.preventDefault();
          setCommandPaletteOpen((prev) => !prev);
          break;
        case "Escape":
          if (commandPaletteOpen) {
            setCommandPaletteOpen(false);
          }
          break;
        case "r":
        case "R":
          if (!e.metaKey && !e.ctrlKey) {
            handlers.onResetZoom?.();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlers, commandPaletteOpen]);

  return {
    commandPaletteOpen,
    setCommandPaletteOpen,
  };
}
